import { CommonModule } from "@angular/common";
import {
  ChangeDetectionStrategy,
  Component,
  WritableSignal,
  inject,
  signal,
} from "@angular/core";
import { HssBuilderComponent } from "@features/history/hss-builder/hss-builder.component";
import { RequestState } from "@shared/models";
import { GlobalService, ScreenSizeService } from "@shared/services";
import { ProgressBarModule } from "primeng/progressbar";
import { SliderModule } from "primeng/slider";
import { TimelineModule } from "primeng/timeline";
import {
  BehaviorSubject,
  combineLatest,
  map,
  Observable,
  switchMap,
  tap,
} from "rxjs";
import { Hss } from "./hss-builder/models/hss";
import { History, RapItem } from "./hss-builder/models/history";
import { ActivatedRoute, Router } from "@angular/router";
import { HistoryService } from "./services/history.service";
import { ButtonModule } from "primeng/button";
import { FormsModule } from "@angular/forms";
import { InputTextModule } from "primeng/inputtext";
import { ProgressSpinnerModule } from "primeng/progressspinner";
import { AccordionModule } from "primeng/accordion";
import { HssRawFiltered } from "./hss-builder/models/hss-raw-filtered";
import { InputSwitchModule } from "primeng/inputswitch";
import { DividerModule } from "primeng/divider";
import { OverlayPanelModule } from "primeng/overlaypanel";
import { convertDotTimeToMinutes } from "@shared/utilities/functions/time-functions";
import { AccordionComponent, LastUpdatedComponent } from "@shared/components";
import { ADD_CODES, REMOVE_CODES } from "@shared/misc";
import { DateTime } from "luxon";

interface EmpHistoryItem {
  name: string;
  empNum: string;
  addCode: string;
  removeCode: string;
  date: string;
  index: number;
}

interface EmpHistory {
  items: EmpHistoryItem[];
}

@Component({
  selector: "app-history",
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    HssBuilderComponent,
    ProgressBarModule,
    HssBuilderComponent,
    SliderModule,
    ButtonModule,
    InputTextModule,
    ProgressSpinnerModule,
    AccordionModule,
    InputSwitchModule,
    ButtonModule,
    DividerModule,
    LastUpdatedComponent,
    TimelineModule,
    AccordionComponent,
    OverlayPanelModule,
  ],
  templateUrl: "./history.component.html",
  styleUrl: "./history.component.scss",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HistoryComponent {
  globalService = inject(GlobalService);
  screenSizeService = inject(ScreenSizeService);
  route = inject(ActivatedRoute);
  router = inject(Router);
  historyService = inject(HistoryService);

  // updateIndex uses getter, setter, and observable.
  // This allows two-way binding and also triggers hss change
  // when observable changes.
  private _updateIndex = 0;

  get updateIndex(): number {
    return this._updateIndex;
  }

  set updateIndex(newValue: number) {
    this._updateIndex = newValue;
    this.updateIndex$.next(newValue);
  }

  private _enhancedMode = false;

  get enhancedMode(): boolean {
    return this._enhancedMode;
  }

  set enhancedMode(newValue: boolean) {
    this._enhancedMode = newValue;
    localStorage.setItem("enhancedMode", JSON.stringify(newValue));
  }

  updateIndex$ = new BehaviorSubject<number>(0);
  updates$?: Observable<RequestState<History>>;
  // Setting refresh$ to true, forces refresh whereas false uses cache system.
  refresh$ = new BehaviorSubject<boolean>(false);
  hss$?: Observable<Hss | null>;

  afIndices: number[] = [];
  currentAfIndex = -1;
  roIndices: number[] = [];
  currentRoIndex = -1;
  cxlIndices: number[] = [];
  currentCxlIndex = -1;
  pilotIndices: number[] = [];
  currentPilotIndex = -1;

  isLoading = signal(false);
  sequenceNumber: string = this.route.snapshot.params["sequenceNumber"];
  date: string = this.route.snapshot.params["date"];
  seat: string = this.route.snapshot.params["seat"];
  sequenceSummaryData = {
    sequenceNumber: this.sequenceNumber,
    date: this.date,
    seat: this.seat,
  };
  updatesCount = 0;

  fontSizeOffset = signal(0);

  startingTl = signal("");
  endingTl = signal("");
  diffTl = signal("");
  stationTimeZones: WritableSignal<Record<string, string>> = signal({ "": "" });
  raps: WritableSignal<RapItem[]> = signal([]);

  empHistory = signal<EmpHistory>({ items: [] });

  ngOnInit(): void {
    const specifiedUpdateId = this.route.snapshot.queryParams["updateid"];
    let isInitialLoad = true;

    this.loadSavedSettings();

    this.updates$ = this.refresh$.pipe(
      switchMap((refresh) =>
        this.historyService.getHssHistory(
          this.sequenceNumber,
          this.date,
          this.seat,
          refresh,
        ),
      ),
      tap((response) => {
        const hssList = response?.response?.rawAndFilteredHssList;

        if (hssList) {
          this.setExtraInfo(hssList);
        }
      }),
    );

    this.hss$ = combineLatest([this.updates$, this.updateIndex$]).pipe(
      map(([updates, updateIndex]) => {
        // console.log(updates);
        this.isLoading.set(updates.isLoading);

        if (!updates.isLoading && updates.response?.rawAndFilteredHssList) {
          this.stationTimeZones.set(updates.response.stationTimeZones);
          this.raps.set(updates.response.raps);
          const hssList = updates.response.rawAndFilteredHssList;

          this.updatesCount = hssList.length;
          this.updateCategoryIndices(hssList);

          // Find sequenceUpdateId if specified for deep linking.
          if (isInitialLoad && specifiedUpdateId) {
            isInitialLoad = false;
            const specifiedIndex = this.getIndexFromUpdateId(
              parseInt(specifiedUpdateId),
              updates.response.rawAndFilteredHssList,
            );

            if (specifiedIndex > -1) {
              this.updateIndex = specifiedIndex;
              return hssList[specifiedIndex]?.filtered;
            }
          }

          return hssList[updateIndex]?.filtered;
        }
        return null;
      }),
    );

    this.refresh$.next(true);
  }

  // Convert timestamp to base timezone.
  getHbtTimestamp(timestampUtc: string, base: string): string {
    const timestampDateTime = DateTime.fromFormat(
      timestampUtc,
      "MM/dd/yyyy HH:mm:ss",
      { zone: "UTC" },
    );

    if (this.stationTimeZones()[base]) {
      return timestampDateTime
        .setZone(this.stationTimeZones()[base])
        .toFormat("MM/dd/yyyy HH:mm:ss");
    }

    return "Timestamp error";
  }

  onRefreshClick(): void {
    this.refresh$.next(true);
  }

  incrementUpdateIndex(): void {
    if (this.updateIndex < this.updatesCount - 1) {
      this.updateIndex += 1;
    }

    this.currentRoIndex = -1;
    this.currentPilotIndex = -1;
    this.currentCxlIndex = -1;
    this.currentAfIndex = -1;
  }

  decrementUpdateIndex(): void {
    if (this.updateIndex > 0) {
      this.updateIndex -= 1;
    }

    this.currentRoIndex = -1;
    this.currentPilotIndex = -1;
    this.currentCxlIndex = -1;
    this.currentAfIndex = -1;
  }

  increaseFontSize(): void {
    const currentOffset = this.fontSizeOffset();
    localStorage.setItem("fontSizeOffset", `${currentOffset + 1}`);
    this.fontSizeOffset.set(currentOffset + 1);
  }

  decreaseFontSize(): void {
    const currentOffset = this.fontSizeOffset();
    localStorage.setItem("fontSizeOffset", `${currentOffset - 1}`);
    this.fontSizeOffset.set(currentOffset - 1);
  }

  onPilotHistoryClick(index: number): void {
    this.updateIndex = index;
  }

  getCodeDescription(code: string, addOrRemove: "add" | "remove"): string {
    if (addOrRemove === "add") {
      return ADD_CODES[code.trim()] || "Unknown";
    } else {
      return REMOVE_CODES[code.trim()] || "Unknown";
    }
  }

  // Populate extra info.
  setExtraInfo(hssList: HssRawFiltered[]): void {
    if (hssList.length === 0) {
      return;
    }

    const startingTl = hssList[0].filtered.summary.totalPay.text;
    const endingTl = hssList[hssList.length - 1].filtered.summary.totalPay.text;
    const diffMinutes =
      convertDotTimeToMinutes(endingTl) - convertDotTimeToMinutes(startingTl);
    let diffTl = "0";

    if (diffMinutes !== 0) {
      diffTl = `${diffMinutes < 0 ? "-" : ""}${Math.floor(Math.abs(diffMinutes) / 60)}:${Math.abs(
        diffMinutes % 60,
      )
        .toFixed(0)
        .padStart(2, "0")}`;
    }

    this.startingTl.set(startingTl.replace(".", ":"));
    this.endingTl.set(endingTl.replace(".", ":"));
    this.diffTl.set(diffTl);
  }

  nextAf(): void {
    this.currentRoIndex = -1;
    this.currentPilotIndex = -1;
    this.currentCxlIndex = -1;

    if (this.afIndices.length > 0) {
      let found = false;

      let count = 0;
      for (const index of this.afIndices) {
        count++;

        if (index > this.updateIndex) {
          this.updateIndex = index;
          this.currentAfIndex = count;
          found = true;
          break;
        }
      }

      if (!found) {
        this.updateIndex = this.afIndices[0];
        this.currentAfIndex = 1;
      }
    }
  }

  getAfBadge(): string {
    if (this.currentAfIndex > 0) {
      return `${this.currentAfIndex} / ${this.afIndices.length}`;
    }
    return this.afIndices.length.toString();
  }

  nextRo(): void {
    this.currentAfIndex = -1;
    this.currentPilotIndex = -1;
    this.currentCxlIndex = -1;

    if (this.roIndices.length > 0) {
      let found = false;

      let count = 0;
      for (const index of this.roIndices) {
        count++;

        if (index > this.updateIndex) {
          this.updateIndex = index;
          this.currentRoIndex = count;
          found = true;
          break;
        }
      }

      if (!found) {
        this.updateIndex = this.roIndices[0];
        this.currentRoIndex = 1;
      }
    }
  }

  getRoBadge(): string {
    if (this.currentRoIndex > 0) {
      return `${this.currentRoIndex} / ${this.roIndices.length}`;
    }
    return this.roIndices.length.toString();
  }

  nextCxl(): void {
    this.currentAfIndex = -1;
    this.currentRoIndex = -1;
    this.currentPilotIndex = -1;

    if (this.cxlIndices.length > 0) {
      let found = false;

      let count = 0;
      for (const index of this.cxlIndices) {
        count++;

        if (index > this.updateIndex) {
          this.updateIndex = index;
          this.currentCxlIndex = count;
          found = true;
          break;
        }
      }

      if (!found) {
        this.updateIndex = this.cxlIndices[0];
        this.currentCxlIndex = 1;
      }
    }
  }

  getCxlBadge(): string {
    if (this.currentCxlIndex > 0) {
      return `${this.currentCxlIndex} / ${this.cxlIndices.length}`;
    }
    return this.cxlIndices.length.toString();
  }

  nextPilot(): void {
    this.currentAfIndex = -1;
    this.currentRoIndex = -1;
    this.currentCxlIndex = -1;

    if (this.pilotIndices.length > 0) {
      let found = false;

      let count = 0;
      for (const index of this.pilotIndices) {
        count++;

        if (index > this.updateIndex) {
          this.updateIndex = index;
          this.currentPilotIndex = count;
          found = true;
          break;
        }
      }

      if (!found) {
        this.updateIndex = this.pilotIndices[0];
        this.currentPilotIndex = 1;
      }
    }
  }

  getPilotBadge(): string {
    if (this.currentPilotIndex > 0) {
      return `${this.currentPilotIndex} / ${this.pilotIndices.length}`;
    }
    return this.pilotIndices.length.toString();
  }

  onBackClicked(): void {
    this.router.navigate(["/"]);
  }

  private loadSavedSettings(): void {
    const savedEnhancedMode = localStorage.getItem("enhancedMode");

    if (savedEnhancedMode !== null) {
      this.enhancedMode = JSON.parse(savedEnhancedMode);
    }

    const fontSizeOffset = localStorage.getItem("fontSizeOffset");

    if (fontSizeOffset !== null) {
      this.fontSizeOffset.set(JSON.parse(fontSizeOffset));
    }
  }

  // Returns the index given a sequenceUpdateId. Used for deep linking.
  private getIndexFromUpdateId(
    updateId: number,
    updates: HssRawFiltered[],
  ): number {
    for (let i = 0; i < updates.length; i++) {
      if (updates[i].filtered.sequenceUpdateId === updateId) {
        return i;
      }
    }

    return -1;
  }

  // Update the count and indices for "jump-to" categories.
  // Also populates the empHistory list.
  private updateCategoryIndices(updates: HssRawFiltered[]): void {
    const afIndices: number[] = [];
    const roIndices: number[] = [];
    const pilotIndices: number[] = [];
    const cxlIndices: number[] = [];
    const empHistory: EmpHistoryItem[] = [];

    for (let i = 0; i < updates.length; i++) {
      if (updates[i].filtered.isAf) {
        afIndices.push(i);
      }
      if (updates[i].filtered.isRo) {
        roIndices.push(i);
      }
      if (updates[i].filtered.isPilotChange) {
        pilotIndices.push(i);
        empHistory.push({
          name: updates[i].filtered.header.name.text,
          empNum: updates[i].filtered.header.empNum.text,
          date: `${updates[i].filtered.timestampHbt} (${updates[i].filtered.header.base.text})`,
          index: i,
          addCode: updates[i].filtered.header.addCode.text,
          removeCode: updates[i].filtered.header.removeCode.text,
        });
      }
      if (updates[i].filtered.isCxl) {
        cxlIndices.push(i);
      }
    }

    this.afIndices = afIndices;
    this.roIndices = roIndices;
    this.pilotIndices = pilotIndices;
    this.cxlIndices = cxlIndices;
    this.empHistory.set({ items: empHistory });
  }
}
